import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BlueChatButton from "./BlueChatButton";
import BlueChatWindow from "./BlueChatWindow";
import { resolveConfig } from "../config/blueConfig";
import { resolvePageContext } from "../services/contextProvider";
import { sendToProvider } from "../services/aiProvider";
import "../styles/blue-chat.css";

/**
 * BlueAssistant — composant racine du module.
 *
 * Seul composant que l'application hôte importe.
 *
 * Props :
 *   - config          : surcharge partielle de la configuration (blueConfig.js)
 *   - pageContext     : objet de contexte statique de la page
 *   - getPageContext  : fonction retournant le contexte (utile en SPA)
 *
 * L'état de la conversation est conservé pendant toute la durée
 * de la session courante (montage du composant). Aucune persistance
 * localStorage : rouvrir la page réinitialise la conversation.
 */
export function BlueAssistant({ config: userConfig, pageContext, getPageContext }) {
  const config = useMemo(() => resolveConfig(userConfig), [userConfig]);

  const [open, setOpen] = useState(!!config.openByDefault);
  const [state, setState] = useState("idle"); // "idle" | "thinking"
  const [messages, setMessages] = useState(() => [
    { id: "greet", role: "blue", content: config.greeting },
  ]);
  const idCounter = useRef(1);
  const historyRef = useRef(messages);
  historyRef.current = messages;

  const nextId = () => `m-${++idCounter.current}`;

  const handleSend = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || state === "thinking") return;

      const userMsg = { id: nextId(), role: "user", content: trimmed };
      // Historique AVANT ajout du message courant : sera envoyé au provider.
      // Le message courant est transmis séparément via `message` — le backend
      // est chargé de l'appendre après `history`. Cela évite tout doublon.
      const previousHistory = historyRef.current;
      setMessages((prev) => trimHistory([...prev, userMsg], config.maxHistoryLength));
      setState("thinking");

      try {
        const ctx = resolvePageContext(pageContext, getPageContext);
        // Fenêtre glissante sur l'historique antérieur uniquement.
        const window = config.historyWindowForProvider || previousHistory.length;
        const historySlice = previousHistory.slice(-window);
        const reply = await sendToProvider({
          message: trimmed,
          history: toProviderHistory(historySlice),
          context: ctx,
          config,
        });
        // Le provider http renvoie { reply, sources } ; mock/function renvoie une chaîne.
        const replyText = typeof reply === "string" ? reply : (reply?.reply || "");
        const sources = (reply && typeof reply === "object" && Array.isArray(reply.sources)) ? reply.sources : [];
        const cleaned = (replyText || "").trim();
        const blueMsg = {
          id: nextId(),
          role: "blue",
          content: cleaned || config.strings.emptyReplyLabel,
          sources,
        };
        setMessages((prev) =>
          trimHistory([...prev, blueMsg], config.maxHistoryLength)
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[Blue] provider error", e);
        const errMsg = { id: nextId(), role: "blue", content: config.strings.errorLabel };
        setMessages((prev) => trimHistory([...prev, errMsg], config.maxHistoryLength));
      } finally {
        setState("idle");
      }
    },
    [config, pageContext, getPageContext, state]
  );

  // Escape ferme la fenêtre (retour au bouton, la conversation est conservée).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Marque le root non-visible si l'onglet passe en arrière-plan (perf).
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!config.pauseWhenHidden) return;
    const onVis = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [config.pauseWhenHidden]);

  return (
    <div
      className={`blue-chat-root blue-chat-root--${config.position}`}
      data-testid="blue-chat-root"
      data-open={open ? "true" : "false"}
      data-visible={visible ? "true" : "false"}
    >
      {open ? (
        <BlueChatWindow
          config={config}
          messages={messages}
          state={state}
          onSend={handleSend}
          onClose={() => setOpen(false)}
        />
      ) : (
        <BlueChatButton
          config={config}
          onOpen={() => setOpen(true)}
          unreadIntro={messages.length <= 1}
        />
      )}
    </div>
  );
}

function trimHistory(list, max) {
  if (list.length <= max) return list;
  // Conserve le premier message (accueil) + la fin de la conversation.
  return [list[0], ...list.slice(-max + 1)];
}

function toProviderHistory(messages) {
  return messages.map((m) => ({ role: m.role, content: m.content }));
}

export default BlueAssistant;
